/// <amd-module name="Likes/LikeActionCreator" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";

import { LikeActions } from "Likes/ActionsHub";
import { SocialEngagementRecord } from "Likes/Contracts/Social";
import { LikeArtifactInfo } from "Likes/LikeModels";
import { LikeSource } from "Likes/LikeSource";
import { SocialTelemetry } from "Likes/SocialTelemetry";

/**
 * Action Creator for Like information
 */
export class LikeActionCreator {
    private readonly _socialTelemetry: SocialTelemetry;

    constructor(
        private _actions: LikeActions,
        private readonly _pageContext: IVssPageContext,
        private _likeSource?: LikeSource) {
        this._socialTelemetry = new SocialTelemetry(this._pageContext);
    }

    /**
     * Loads social engagement information for the Like, and creates an action with likeState as payload.
     */
    public getLikes(likeArtifactInfo: LikeArtifactInfo): Promise<void> {
        return this.likeSource.getLikes(likeArtifactInfo).then(
            (response) => {
                this._invokeDataReceivedAction(response);
                return;
            },
            (error: Error) => {
                // no-op
            });
    }

    /**
     * Loads users information of those who liked the artifact, and creates an action with usersInfo as payload.
     */
    public fetchLikedUsers(likeArtifactInfo: LikeArtifactInfo): void {
        this.likeSource.getLikedUsers(likeArtifactInfo).then(
            (response: IdentityRef[]) => {
                this._invokeLikedUsersInfoReceivedAction(response);
            },
            (error: Error) => {
                // no-op
            });
    }

    /**
     * Deletes social engagement information for the Like, and creates an action with resultant likeState as payload.
     */
    public deleteLike(likeArtifactInfo: LikeArtifactInfo): void {
        this._socialTelemetry.logToggleLikeClicked(likeArtifactInfo.artifactType, false);
        this._actions.deleteLikeStarted.invoke(void 0);

        this.likeSource.deleteLike(likeArtifactInfo).then(
            (response: SocialEngagementRecord) => {
                this.fetchLikedUsers(likeArtifactInfo);
            },
            (error: Error) => {
                // incase of error, update the store with right values.
                this.getLikes(likeArtifactInfo);
                this._actions.deleteLikeFailed.invoke(error);
            });
    }


    /**
     * Creates social engagement information for the Like, and creates an action with resultant likeState as payload.
     */
    public createLike(likeArtifactInfo: LikeArtifactInfo): void {
        this._socialTelemetry.logToggleLikeClicked(likeArtifactInfo.artifactType, true);
        this._actions.createLikeStarted.invoke(void 0);
        this.likeSource.createLike(likeArtifactInfo).then(
            (response: SocialEngagementRecord) => {
                this.fetchLikedUsers(likeArtifactInfo);
            },
            (error: Error) => {
                // incase of error, update the store with right values.
                this.getLikes(likeArtifactInfo);
                this._actions.createLikeFailed.invoke(error);
            });
    }

    private _invokeDataReceivedAction(response: SocialEngagementRecord): void {
        let likedFromResponse = false;

        if (response.creationDate != null) {
            likedFromResponse = true;
        }

        this._actions.likeInfoReceived.invoke({
            likeCount: response.socialEngagementStatistics.userCount,
            isLiked: likedFromResponse,
        });
    }

    private _invokeLikedUsersInfoReceivedAction(usersInfo: IdentityRef[]): void {
        this._actions.likedUsersInfoReceived.invoke(usersInfo.map(user => user.displayName));
    }

    private get likeSource(): LikeSource {
        if (!this._likeSource) {
            this._likeSource = new LikeSource(this._pageContext);
        }

        return this._likeSource;
    }
}
